/**
 * Project "Tracking" tab — single place for rank + Search Console measurement.
 */
import { useMemo, useState } from 'react';
import {
  AlertTriangle,
  Download,
  Loader2,
  Play,
  Radar,
  Search,
  TrendingDown,
  TrendingUp,
} from 'lucide-react';
import { toast } from 'sonner';
import type { Project } from '@seo/types';
import { Button } from '@/components/ui/button';
import { Callout } from '@/components/ui/callout';
import { EmptyState } from '@/components/ui/empty';
import { ErrorBanner } from '@/components/error-banner';
import { Spinner } from '@/components/spinner';
import { Surface } from '@/components/ui/surface';
import { StatTile, type StatTone } from '@/components/ui/stat-tile';
import {
  Section,
  SectionDescription,
  SectionHeader,
  SectionTitle,
  SectionToolbar,
} from '@/components/ui/section';
import { cn } from '@/lib/cn';
import { summarizeApiError } from '@/lib/api';
import { useAuthStore } from '@/lib/auth';
import { downloadAuthed } from '@/lib/download';
import { GscInsightsCards } from '@/features/integrations/gsc-insights-cards';
import { KeywordRow } from '@/features/ranks/components/keyword-row';
import { TrackKeywordButton } from '@/features/ranks/components/track-keyword-button';
import { useTrackedKeywords } from '@/features/ranks/use-tracked-keywords';
import { useRankSummary } from '@/features/ranks/use-rank-summary';
import {
  isCheckedSince,
  useStartRankCheck,
} from '@/features/ranks/use-start-rank-check';
import { useDeleteTrackedKeyword } from '@/features/ranks/use-delete-tracked-keyword';
import { CriticalsDeltaCard } from '@/features/actions/components/criticals-delta-card';
import { ImpactCards } from '@/features/actions/components/impact-cards';

export function TrackingPanel({
  projectId,
  project,
  /** Hide the page-level title when already wrapped (e.g. Strategy → Tracking). */
  hideIntro = false,
}: {
  projectId: string;
  project: Project;
  hideIntro?: boolean;
}): JSX.Element {
  const tracked = useTrackedKeywords(projectId);
  const summary = useRankSummary(projectId);
  const check = useStartRankCheck();
  const del = useDeleteTrackedKeyword(projectId);
  const items = tracked.data?.items ?? [];
  const sum = summary.data;
  const checking = check.isChecking && check.checkingProjectId === projectId;
  const checkStartedAt = checking ? check.checkStartedAt : null;
  const checkingTrackedKeywordId = checking ? check.checkingTrackedKeywordId : null;
  const focusedKeyword = useMemo(
    () =>
      checkingTrackedKeywordId
        ? (items.find((r) => r.id === checkingTrackedKeywordId)?.keyword ?? null)
        : null,
    [checkingTrackedKeywordId, items],
  );

  const uncheckedCount = useMemo(
    () => items.filter((r) => r.last_checked_at == null).length,
    [items],
  );

  // Only surface stat tiles that carry information. When everything is zero
  // we hide the strip — five "0" tiles are pure clutter for new trackers.
  const statTiles = useMemo(() => {
    type Tile = {
      label: string;
      value: number;
      icon: typeof Search;
      tone?: StatTone;
    };
    const tiles: Tile[] = [];
    const trackedCount = sum?.tracked_count ?? items.length;
    if (trackedCount > 0) {
      tiles.push({ label: 'Tracked', value: trackedCount, icon: Search });
    }
    if ((sum?.moved_up ?? 0) > 0) {
      tiles.push({
        label: 'Moved up · 7d',
        value: sum!.moved_up,
        icon: TrendingUp,
        tone: 'success',
      });
    }
    if ((sum?.moved_down ?? 0) > 0) {
      tiles.push({
        label: 'Moved down · 7d',
        value: sum!.moved_down,
        icon: TrendingDown,
        tone: 'danger',
      });
    }
    if ((sum?.drop_alerts?.length ?? 0) > 0) {
      tiles.push({
        label: 'Drop alerts',
        value: sum!.drop_alerts.length,
        icon: AlertTriangle,
        tone: 'warning',
      });
    }
    return tiles;
  }, [sum, items.length]);

  // Progress numerator/denominator. When scoped to a single keyword the
  // total is 1 (not `items.length`) so the banner reads "1 of 1" not "1 of N".
  const { pendingCheckCount, checkTotal } = useMemo(() => {
    if (!checking || !checkStartedAt) return { pendingCheckCount: 0, checkTotal: 0 };
    if (checkingTrackedKeywordId) {
      const row = items.find((r) => r.id === checkingTrackedKeywordId);
      const done = row ? isCheckedSince(row, checkStartedAt) : false;
      return { pendingCheckCount: done ? 0 : 1, checkTotal: 1 };
    }
    const pending = items.filter((r) => !isCheckedSince(r, checkStartedAt)).length;
    return { pendingCheckCount: pending, checkTotal: items.length };
  }, [checking, checkStartedAt, checkingTrackedKeywordId, items]);

  const startCheck = async (opts?: {
    quiet?: boolean;
    focusKeyword?: string;
    trackedKeywordId?: string;
  }) => {
    try {
      if (!opts?.quiet) {
        toast.message('Checking Google positions…', {
          description: opts?.focusKeyword
            ? `Looking up “${opts.focusKeyword}”. This usually takes a few seconds.`
            : 'Looking up your tracked keywords. This usually takes a few seconds.',
        });
      }
      const result = await check.mutateAsync(
        opts?.trackedKeywordId
          ? { projectId, trackedKeywordId: opts.trackedKeywordId }
          : projectId,
      );
      if (result.outcome === 'timed_out') {
        toast.warning('Still waiting on Google', {
          description: 'Results may appear shortly — try refreshing if a row stays on “Never”.',
        });
      } else if (!opts?.quiet) {
        toast.success('Positions updated');
      }
    } catch (e) {
      const err = summarizeApiError(e);
      toast.error(err.title, { description: err.description });
    }
  };

  return (
    <div className="space-y-6">
      {!hideIntro ? (
        <div className="space-y-1">
          <h2 className="text-base font-semibold tracking-tight">Tracking</h2>
          <p className="text-sm text-muted-foreground">
            You pick keywords to watch. We check where your site ranks on Google, then show
            week-over-week moves — so you can see if briefs and fixes actually helped.
          </p>
        </div>
      ) : null}

      {(() => {
        // Total tiles = the conditional stat tiles + the always-on CriticalsDeltaCard.
        const totalTiles = statTiles.length + 1;
        return (
          <div
            className={cn(
              'grid gap-3',
              totalTiles >= 5
                ? 'grid-cols-2 lg:grid-cols-5'
                : totalTiles === 4
                  ? 'grid-cols-2 lg:grid-cols-4'
                  : totalTiles === 3
                    ? 'grid-cols-3'
                    : totalTiles === 2
                      ? 'grid-cols-2'
                      : 'grid-cols-1',
            )}
          >
            {statTiles.map((tile) => (
              <StatTile
                key={tile.label}
                label={tile.label}
                value={tile.value}
                icon={tile.icon}
                tone={tile.tone}
              />
            ))}
            <CriticalsDeltaCard projectId={projectId} />
          </div>
        );
      })()}

      {checking ? (
        <Callout
          tone="info"
          icon={Radar}
          title={
            focusedKeyword
              ? `Checking “${focusedKeyword}” on Google`
              : 'Checking Google positions'
          }
        >
          <p className="text-sm text-muted-foreground">
            {focusedKeyword
              ? pendingCheckCount > 0
                ? 'Usually takes a few seconds. Your other keywords are not being re-checked.'
                : 'Almost done — updating the row now.'
              : pendingCheckCount > 0
                ? `${checkTotal - pendingCheckCount} of ${checkTotal} done. This usually takes a few seconds per keyword.`
                : 'Almost done — updating rows now.'}
          </p>
        </Callout>
      ) : null}

      {(sum?.drop_alerts?.length ?? 0) > 0 ? (
        <Callout
          tone="warning"
          title={`${sum!.drop_alerts.length} keyword${sum!.drop_alerts.length === 1 ? '' : 's'} dropped >5 positions this week`}
        >
          <ul className="mt-1 space-y-1">
            {sum!.drop_alerts.slice(0, 5).map((a) => (
              <li key={a.tracked_keyword_id}>
                <span className="font-medium">{a.keyword}</span>
                {' — '}#{a.previous_position ?? '?'} → #{a.current_position ?? '—'} (−
                {a.drop})
              </li>
            ))}
          </ul>
        </Callout>
      ) : null}

      <Section>
        <SectionHeader>
          <div>
            <SectionTitle>What shipped, what moved</SectionTitle>
            <SectionDescription>
              Every brief, refresh, and tracked keyword — measured at +7d and
              +28d so you can see what actually helped.
            </SectionDescription>
          </div>
        </SectionHeader>
        <ImpactCards projectId={projectId} />
      </Section>

      <Section>
        <SectionHeader>
          <div>
            <SectionTitle>Keyword positions</SectionTitle>
            <SectionDescription>
              {uncheckedCount > 0 && !checking
                ? `${uncheckedCount} not checked yet — click Check now to fetch positions.`
                : sum && (sum.moved_up > 0 || sum.moved_down > 0)
                  ? `${sum.moved_up} moved up, ${sum.moved_down} moved down this week`
                  : items.length === 0
                    ? 'Add keywords you care about, then check their Google position.'
                    : null}
            </SectionDescription>
          </div>
          <SectionToolbar>
            <ExportCsvButton
              projectId={projectId}
              disabled={items.length === 0}
            />
            <Button
              variant="outline"
              size="sm"
              disabled={check.isPending || checking || items.length === 0}
              onClick={() => void startCheck()}
            >
              {checking || check.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Play className="h-4 w-4" />
              )}
              {checking || check.isPending ? 'Checking…' : 'Check now'}
            </Button>
            <TrackKeywordButton
              projects={[project]}
              defaultProjectId={projectId}
              buttonProps={{ size: 'sm' }}
              onCreated={(row) => {
                void startCheck({
                  focusKeyword: row.keyword,
                  trackedKeywordId: row.id,
                });
              }}
            />
          </SectionToolbar>
        </SectionHeader>

        {(tracked.isPending && !tracked.data) || (summary.isPending && !summary.data) ? (
          <div className="flex justify-center py-10">
            <Spinner />
          </div>
        ) : tracked.isError ? (
          <ErrorBanner error={tracked.error} />
        ) : items.length === 0 ? (
          <EmptyState
            icon={Radar}
            title="No keywords tracked yet"
            description="Type a keyword you want to rank for (or pick one you’ve already analyzed). We’ll watch its Google position over time."
            action={
              <TrackKeywordButton
                projects={[project]}
                defaultProjectId={projectId}
                onCreated={(row) => {
                  void startCheck({
                    focusKeyword: row.keyword,
                    trackedKeywordId: row.id,
                  });
                }}
              />
            }
          />
        ) : (
          <Surface className="overflow-hidden p-0">
            <table className="w-full text-sm">
              <thead className="border-b bg-muted/40 text-left text-xs uppercase tracking-wide text-muted-foreground">
                <tr>
                  <th className="px-4 py-2.5 font-medium">Keyword</th>
                  <th className="px-4 py-2.5 font-medium">Position</th>
                  <th className="px-4 py-2.5 font-medium">Previous</th>
                  <th className="px-4 py-2.5 font-medium">7d</th>
                  <th className="px-4 py-2.5 font-medium">30d</th>
                  <th className="px-4 py-2.5 font-medium">Source</th>
                  <th className="px-4 py-2.5 font-medium">Checked</th>
                  <th className="px-4 py-2.5 font-medium" />
                </tr>
              </thead>
              <tbody>
                {items.map((row) => (
                  <KeywordRow
                    key={row.id}
                    projectId={projectId}
                    row={row}
                    awaiting={
                      checking &&
                      !!checkStartedAt &&
                      (!checkingTrackedKeywordId ||
                        row.id === checkingTrackedKeywordId) &&
                      !isCheckedSince(row, checkStartedAt)
                    }
                    deleting={del.isPending}
                    onDelete={async () => {
                      try {
                        await del.mutateAsync(row.id);
                        toast.success(`Stopped tracking “${row.keyword}”`);
                      } catch (e) {
                        const err = summarizeApiError(e);
                        toast.error(err.title, { description: err.description });
                      }
                    }}
                  />
                ))}
              </tbody>
            </table>
          </Surface>
        )}
      </Section>

      <GscInsightsCards projectId={projectId} />
    </div>
  );
}

function ExportCsvButton({
  projectId,
  disabled,
}: {
  projectId: string;
  disabled: boolean;
}): JSX.Element {
  const [pending, setPending] = useState(false);
  const { accessToken, tenant } = useAuthStore();

  const onClick = async (): Promise<void> => {
    setPending(true);
    try {
      const isoDate = new Date().toISOString().slice(0, 10);
      await downloadAuthed({
        url: `/v1/tracked-keywords.csv?project_id=${encodeURIComponent(projectId)}`,
        filename: `tracked-keywords-${projectId.slice(0, 8)}-${isoDate}.csv`,
        accessToken,
        tenantId: tenant?.id,
      });
    } catch (e) {
      const err = summarizeApiError(e);
      toast.error(err.title, { description: err.description });
    } finally {
      setPending(false);
    }
  };

  return (
    <Button
      variant="outline"
      size="sm"
      disabled={disabled || pending}
      onClick={() => void onClick()}
    >
      {pending ? (
        <Loader2 className="h-4 w-4 animate-spin" />
      ) : (
        <Download className="h-4 w-4" />
      )}
      Export CSV
    </Button>
  );
}

